/**
 * ListingDetailsForm — every editable field of a marketplace listing
 * EXCEPT pricing + legal acks. Used by:
 *
 *   * the publish wizard's first step
 *   * the standalone EditListingPage (full-page edit, no wizard)
 *
 * The form is intentionally controlled (parent owns ``form`` + ``set``)
 * so both the wizard's stepper and the edit page can plug in their
 * own state container without forking layout / validation logic.
 *
 * Sub-pieces:
 *   * SingleCategoryPicker - cascading drill-in, must end on a leaf
 *   * IconUploader         - drag/drop + click-to-upload, posts to BE
 *   * TagInput             - chip-based input with Enter-to-add
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress,
  FormControl, Grid, InputLabel, MenuItem, Select,
  Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  fetchRequest as uploadIconRequest,
  clearState as clearIconUpload,
} from '../../redux/actions/marketplace/iconUpload';
import { buildCategoryIndex } from './marketplaceUtils';
import {
  KIND_OPTIONS, UPDATE_FREQUENCY_OPTIONS, DATA_INTERVAL_OPTIONS,
  SLA_OPTIONS,
} from './marketplaceUtils';


// ---------------------------------------------------------------------------
// Single-category picker - chain of dependent dropdowns.
//
// Renders one Select per level of the taxonomy. The first dropdown
// shows the top-level (root) categories; each subsequent dropdown
// only appears AFTER its parent is picked AND only if that parent
// has children. The chain stops when the user lands on a leaf - at
// that point no further dropdown is rendered and ``value`` is the
// leaf slug. Picking a NEW value at any level clears every level
// below it (so the user can change their mind without leaving stale
// selections).
//
// Single label "Category *" floats above the row of dropdowns so the
// whole row reads as one form field.
// ---------------------------------------------------------------------------

const SingleCategoryPicker = ({ categories, value, onChange }) => {
  const { childrenByParent, bySlug } = useMemo(
    () => buildCategoryIndex(categories || []),
    [categories],
  );

  // The picker keeps its OWN drill path so we only commit to the
  // form (``onChange``) when the user lands on a true leaf. That
  // means ``form.category`` is always either empty or a valid leaf
  // slug - the wizard's "Next" button can stay strict ("category
  // required") without inventing a separate "is the slug a leaf"
  // check.
  //
  // The internal trail is seeded from ``value`` so editing an
  // existing listing hydrates the chain correctly.
  const trailFromValue = useMemo(() => {
    if (!value) return [];
    const out = [];
    let cur = bySlug[value];
    while (cur) {
      out.unshift(cur.slug);
      cur = cur.parent_slug ? bySlug[cur.parent_slug] : null;
    }
    return out;
  }, [value, bySlug]);

  const [trail, setTrail] = useState(trailFromValue);
  // Re-hydrate when ``value`` changes from outside (e.g. parent
  // resets the form, or edit page swaps to a different listing).
  useEffect(() => { setTrail(trailFromValue); }, [trailFromValue]);

  // Compute each dropdown's options. Level 0 is roots; level N is
  // children of trail[N-1]. Always add ONE extra dropdown beyond
  // the current trail (until the trail ends on a leaf) so the user
  // always has a next step to drill into.
  const levels = useMemo(() => {
    const out = [];
    out.push({
      parentSlug: null,
      options:    childrenByParent.__root__ || [],
      selected:   trail[0] || '',
    });
    trail.forEach((slug, i) => {
      const kids = childrenByParent[slug] || [];
      if (kids.length === 0) return;
      out.push({
        parentSlug: slug,
        options:    kids,
        selected:   trail[i + 1] || '',
      });
    });
    return out;
  }, [childrenByParent, trail]);

  const isLeaf = (slug) => !!slug
    && !(childrenByParent[slug] || []).length;

  // Picking ``slug`` at level ``levelIdx`` truncates the trail to
  // that level and pushes the new slug. We only call ``onChange``
  // when the trail ends on a leaf - otherwise the form stays at
  // "no category yet" and the wizard's required check keeps Next
  // disabled.
  const handlePick = (slug, levelIdx) => {
    const nextTrail = trail.slice(0, levelIdx);
    if (slug) nextTrail.push(slug);
    setTrail(nextTrail);
    const tail = nextTrail[nextTrail.length - 1] || '';
    onChange(isLeaf(tail) ? tail : '');
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      sx={{ rowGap: 1 }}
    >
      {levels.map((lvl, i) => (
        <FormControl
          key={`${lvl.parentSlug || 'root'}-${i}`}
          size="small"
          sx={{
            minWidth: 180, flex: '1 1 180px',
            '& .MuiOutlinedInput-root': { fontSize: 12.5 },
          }}
        >
          <Select
            displayEmpty
            value={lvl.selected || ''}
            onChange={(e) => handlePick(e.target.value, i)}
            renderValue={(v) => {
              if (!v) {
                const placeholder = i === 0
                  ? 'Pick a category…'
                  : i === 1
                    ? 'Pick a sub-category…'
                    : `Pick a level ${i + 1} sub-category…`;
                return (
                  <Box component="span" sx={{
                    color: 'text.disabled', fontStyle: 'italic',
                  }}>
                    {placeholder}
                  </Box>
                );
              }
              const opt = lvl.options.find((o) => o.slug === v);
              return opt?.name || v;
            }}
          >
            {lvl.options.map((c) => (
              <MenuItem key={c.slug} value={c.slug}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}
    </Stack>
  );
};


// ---------------------------------------------------------------------------
// Icon uploader — click / drag-drop a small image, POSTs to the BE,
// stores the returned URL on the form. Falls back to "no icon" with
// the listing's category glyph everywhere icons are rendered.
// ---------------------------------------------------------------------------

const IconUploader = ({ value, onChange, disabled, compact = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    response: uploadResp,
    isLoading: busy,
    error: err,
  } = useSelector((s) => s.marketplace_icon_upload);
  const inputRef = useRef(null);

  const upload = (file) => {
    if (!file) return;
    dispatch(uploadIconRequest({ file }));
  };

  // Hand the uploaded URL to the form, then clear the slice so the
  // next upload (or another uploader instance) starts fresh.
  useEffect(() => {
    if (!uploadResp?.icon_url) return;
    onChange(uploadResp.icon_url);
    dispatch(clearIconUpload());
  }, [uploadResp, onChange, dispatch]);

  // The old uploader kept busy/err per instance; clear the shared
  // slice on unmount so a stale error doesn't greet the next mount.
  useEffect(() => () => { dispatch(clearIconUpload()); }, [dispatch]);

  // Compact = inline avatar-only mode (used in the Title row). Click
  // the avatar to upload / replace; right-click is a stretch goal.
  if (compact) {
    return (
      <>
        <input
          ref={inputRef} type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
          hidden onChange={(e) => upload(e.target.files?.[0])}
        />
        <Box
          onClick={() => !disabled && inputRef.current?.click()}
          title={value ? 'Replace icon' : 'Upload icon'}
          sx={{
            // Sized to sit cleanly inside a small TextField as an
            // adornment without blowing up the field's intrinsic
            // height (~36px).
            width: 26, height: 26, flexShrink: 0,
            borderRadius: 0.75,
            border: value
              ? `1px solid ${theme.palette.divider}`
              : `1px dashed ${theme.palette.divider}`,
            bgcolor: value
              ? 'background.paper'
              : alpha(theme.palette.text.primary, 0.02),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: disabled ? 'default' : 'pointer',
            position: 'relative',
            overflow: 'hidden',
            transition: 'border-color 120ms ease',
            '&:hover': {
              borderColor: disabled ? undefined : theme.palette.primary.main,
            },
          }}
        >
          {busy ? (
            <CircularProgress size={12} />
          ) : value ? (
            <Box
              component="img" src={value} alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <CloudUploadIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          )}
        </Box>
      </>
    );
  }

  // Full-size mode - dropzone-style affordance so users immediately
  // see this is an upload area, not a decorative chip.
  const empty = !value;
  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        hidden
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <Box
        onClick={() => !disabled && !busy && inputRef.current?.click()}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 1.5, py: 1.25,
          borderRadius: 1.5,
          border: empty
            ? `1px dashed ${theme.palette.divider}`
            : `1px solid ${theme.palette.divider}`,
          bgcolor: empty
            ? alpha(theme.palette.primary.main, 0.025)
            : 'background.paper',
          cursor: disabled || busy ? 'default' : 'pointer',
          transition: 'border-color 120ms ease, background-color 120ms ease',
          '&:hover': {
            borderColor: disabled ? undefined : theme.palette.primary.main,
            bgcolor: empty
              ? alpha(theme.palette.primary.main, 0.05)
              : alpha(theme.palette.text.primary, 0.02),
          },
        }}
      >
        {/* Thumbnail / placeholder */}
        {value ? (
          <Avatar
            src={value} variant="rounded"
            sx={{
              width: 40, height: 40, flexShrink: 0,
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
        ) : (
          <Box sx={{
            width: 40, height: 40, borderRadius: 1, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'primary.main',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          }}>
            {busy
              ? <CircularProgress size={16} />
              : <CloudUploadIcon sx={{ fontSize: 20 }} />}
          </Box>
        )}

        {/* Title + helper. Behaves like a clickable dropzone label. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 13, fontWeight: 600,
            color: empty ? 'primary.main' : 'text.primary',
            lineHeight: 1.2,
          }}>
            {empty
              ? (busy ? 'Uploading…' : 'Click to upload an icon')
              : 'Icon uploaded'}
          </Typography>
          <Typography variant="caption" color="text.secondary"
                      sx={{ fontSize: 11, lineHeight: 1.3 }}>
            ≤ 2 MB · PNG, JPG, WEBP, GIF or SVG · square images render best
          </Typography>
        </Box>

        {/* Action buttons — only show once the user has uploaded
            something. Click handlers stop propagation so the parent
            dropzone click doesn't re-open the file picker. */}
        {value && !disabled && (
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Button
              size="small" variant="outlined"
              startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
              sx={{ textTransform: 'none', py: 0.4, fontSize: 12 }}
            >
              Replace
            </Button>
            <Button
              size="small" color="error"
              startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              sx={{ textTransform: 'none', py: 0.4, fontSize: 12 }}
            >
              Remove
            </Button>
          </Box>
        )}
      </Box>
      {err && (
        <Alert severity="error" sx={{ mt: 1, py: 0.25 }}>{err}</Alert>
      )}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Tag input — Enter / comma to commit, backspace to remove the last
// chip, click-to-delete on each chip. Caps at 10 tags so cards stay
// readable.
// ---------------------------------------------------------------------------

const MAX_TAGS = 10;

const TagInput = ({ value, onChange, disabled }) => {
  const theme = useTheme();
  const [draft, setDraft] = useState('');
  const tags = Array.isArray(value) ? value : [];

  const commit = (raw) => {
    const t = (raw || '').trim().replace(/^#/, '').toLowerCase();
    if (!t) return;
    if (tags.includes(t)) { setDraft(''); return; }
    if (tags.length >= MAX_TAGS) return;
    onChange([...tags, t]);
    setDraft('');
  };

  const remove = (t) => onChange(tags.filter((x) => x !== t));

  return (
    <Box>
      <Box sx={{
        minHeight: 36, px: 1, py: 0.5,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center',
        '&:focus-within': { borderColor: theme.palette.primary.main },
      }}>
        {tags.map((t) => (
          <Chip
            key={t}
            label={`#${t}`}
            size="small"
            onDelete={disabled ? undefined : () => remove(t)}
            sx={{
              height: 20, fontSize: 11, fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              '& .MuiChip-label': { px: 0.75 },
              '& .MuiChip-deleteIcon': { fontSize: 14 },
            }}
          />
        ))}
        <Box
          component="input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={disabled}
          placeholder={tags.length === 0
            ? 'Add tags — press Enter to add'
            : (tags.length >= MAX_TAGS ? `Max ${MAX_TAGS} tags` : '+ tag')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit(draft);
            } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
              remove(tags[tags.length - 1]);
            }
          }}
          onBlur={() => commit(draft)}
          sx={{
            flex: 1, minWidth: 80,
            border: 'none', outline: 'none', background: 'transparent',
            fontSize: 12, color: theme.palette.text.primary, py: 0.25,
          }}
        />
      </Box>
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------

// Tiny section-header label - matches the floating MUI input label
// font size (12px) so the form reads as one consistent rhythm.
const SectionLabel = ({ children, required }) => (
  <Typography
    component="label"
    sx={{
      display: 'block', mb: 0.6,
      fontSize: 12, fontWeight: 500,
      color: 'text.secondary',
      lineHeight: 1.2,
    }}
  >
    {children}
    {required && <Box component="span" sx={{ color: 'error.main', ml: 0.3 }}>*</Box>}
  </Typography>
);


const ListingDetailsForm = ({
  form,
  set,
  setMany,
  categories,
  signalKey,
  // Suggestion: when the suggest payload lands we silently auto-fill
  // any field the user hasn't touched. There is no "Suggest" button -
  // the user just sees pre-populated values they can edit.
  suggestion,
  // Disable fields the user can't change after publish.
  disableSignalKey = false,
}) => {
  // Auto-apply suggestion ONCE per signal so the user doesn't have to
  // click anything to get the description / kind / cadence prefilled.
  // Subsequent edits are theirs to own.
  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (autoAppliedRef.current) return;
    if (!suggestion) return;
    if (form.description) return;
    autoAppliedRef.current = true;
    setMany({
      description:      suggestion.description || form.description,
      kind:             suggestion.kind || form.kind,
      update_frequency: suggestion.update_frequency || form.update_frequency,
      data_interval:    suggestion.data_interval || form.data_interval,
    });
  }, [suggestion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dropdown helper: when the active value isn't one of the presets
  // we treat it as "custom" and surface a small text field. NOTE:
  // intentionally NOT named ``useCustomFor`` so eslint doesn't think
  // it's a React hook (it isn't - just a pure derivation).
  const isCustomValue = (selectedValue, presetOptions) => {
    if (!selectedValue) return false;
    const isPreset = presetOptions.some((o) => o.value === selectedValue);
    return !isPreset || selectedValue === 'custom';
  };

  // Common shrink props so every TextField / Select has its label
  // floating above the box (like the dropdowns) instead of as a
  // floating placeholder. Keeps row heights identical across all
  // form widgets in the same Grid row.
  const COMPACT_INPUT = {
    size: 'small',
    InputLabelProps: { shrink: true },
    sx: {
      '& .MuiOutlinedInput-root': { fontSize: 12.5 },
      '& .MuiInputLabel-root':    { fontSize: 12 },
    },
  };

  // Cascading dropdown helper that returns a compact <Select>
  // wrapped in the same FormControl shape as a TextField, with the
  // optional "Custom…" follow-up text input.
  const renderDropdown = (label, key, options) => {
    const isCustom = isCustomValue(form[key], options);
    return (
      <FormControl {...COMPACT_INPUT} fullWidth>
        <InputLabel shrink>{label}</InputLabel>
        <Select
          notched label={label}
          value={isCustom ? 'custom' : (form[key] || '')}
          onChange={(e) => set(key,
            e.target.value === 'custom'
              ? (form[`${key}_custom`] || '')
              : e.target.value,
          )}
          displayEmpty
        >
          <MenuItem value=""><em>—</em></MenuItem>
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
        {isCustom && (
          <TextField
            {...COMPACT_INPUT}
            placeholder="Type a custom value"
            value={form[key] === 'custom'
              ? form[`${key}_custom`] || ''
              : form[key]}
            onChange={(e) => {
              set(`${key}_custom`, e.target.value);
              set(key, e.target.value);
            }}
            sx={{ ...COMPACT_INPUT.sx, mt: 0.75 }}
          />
        )}
      </FormControl>
    );
  };

  return (
    <Grid container spacing={2}>
      {/* ── Category (full width) ── */}
      <Grid item xs={12}>
        <SectionLabel required>Category</SectionLabel>
        <SingleCategoryPicker
          categories={categories}
          value={form.category}
          onChange={(slug) => set('category', slug)}
        />
      </Grid>

      {/* ── Title + Signal key ── */}
      <Grid item xs={12} md={8}>
        <TextField
          {...COMPACT_INPUT} fullWidth
          label="Listing title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          {...COMPACT_INPUT} fullWidth
          label="Signal key"
          value={signalKey || form.signal_key}
          disabled
          sx={{
            ...COMPACT_INPUT.sx,
            '& input': { fontFamily: 'monospace', fontSize: 11.5 },
          }}
        />
      </Grid>

      {/* ── Listing icon ── dedicated row with an OBVIOUS upload
            affordance: a dashed thumbnail tile with a cloud icon
            (empty) or the uploaded image (filled), side-by-side with
            an explicit "Upload icon" button + helper text. The
            previous startAdornment version was unreadable as an
            upload control - it just looked like a decoration. */}
      <Grid item xs={12}>
        <SectionLabel>Listing icon (optional)</SectionLabel>
        <IconUploader
          value={form.icon_url}
          onChange={(url) => set('icon_url', url)}
        />
      </Grid>

      {/* ── Description (full width) ── */}
      <Grid item xs={12}>
        <TextField
          {...COMPACT_INPUT} fullWidth multiline minRows={3}
          label="Description"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Auto-populated from your workspace — edit to refine."
        />
      </Grid>

      {/* ── Metadata: 2 cells per row ── */}
      <Grid item xs={12} md={6}>
        <TextField
          {...COMPACT_INPUT} fullWidth
          label="Source"
          value={form.source_label}
          onChange={(e) => set('source_label', e.target.value)}
          placeholder="e.g. NOAA, internal sensors"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl {...COMPACT_INPUT} fullWidth>
          <InputLabel shrink>Data kind</InputLabel>
          <Select
            notched label="Data kind"
            value={form.kind || 'both'}
            onChange={(e) => set('kind', e.target.value)}
          >
            {KIND_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        {renderDropdown('Update frequency', 'update_frequency',
          UPDATE_FREQUENCY_OPTIONS)}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderDropdown('Data interval', 'data_interval',
          DATA_INTERVAL_OPTIONS)}
      </Grid>

      <Grid item xs={12} md={6}>
        {renderDropdown('SLA', 'sla', SLA_OPTIONS)}
      </Grid>
      <Grid item xs={12} md={6}>
        {/* Tags as a "fieldset"-style outlined wrapper so it visually
            matches the surrounding TextFields / Selects. */}
        <Box sx={{ position: 'relative' }}>
          <Typography
            component="label"
            sx={{
              position: 'absolute', top: -8, left: 9,
              px: 0.5, bgcolor: 'background.paper', zIndex: 1,
              fontSize: 12, color: 'text.secondary', lineHeight: 1,
            }}
          >
            Tags
          </Typography>
          <TagInput
            value={form.tags}
            onChange={(t) => set('tags', t)}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default ListingDetailsForm;
export { SingleCategoryPicker, IconUploader, TagInput };
